import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import BusinessProfileSelector from '../components/campaigns/BusinessProfileSelector';
import CampaignForm from '../components/campaigns/CampaignForm';
import CampaignGoalSelector from '../components/campaigns/CampaignGoalSelector';
import WebsitePreview from '../components/campaigns/WebsitePreview';
import ManualInputForm from '../components/campaigns/ManualInputForm';
import GenerationProgress from '../components/campaigns/GenerationProgress';
import { campaignApi, trackEvent } from '../services/campaignApi';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

// Step arrays for different workflows
const newProfileSteps = ['Select Profile Type', 'Enter Website & Goal', 'Analyzing', 'Review & Create', 'Creating Campaign'];
const existingProfileSteps = ['Select Business Profile', 'Campaign Setup', 'Review & Create', 'Creating Campaign'];
const noProfilesSteps = ['Enter Website & Goal', 'Analyzing', 'Review & Create', 'Creating Campaign'];

const CampaignCreatorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [hasExistingProfiles, setHasExistingProfiles] = useState(false);
  const [profilesLoaded, setProfilesLoaded] = useState(false); // Track if profiles have been loaded
  const [workflowType, setWorkflowType] = useState(null); // 'new', 'existing', or null
  const [activeStep, setActiveStep] = useState(0);
  const [selectedBusinessProfile, setSelectedBusinessProfile] = useState(null);
  const [campaignData, setCampaignData] = useState({
    campaignName: '',
    websiteUrl: '',
    campaignGoal: '',
    description: ''
  });
  const [websiteAnalysis, setWebsiteAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [error, setError] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [createdCampaignId, setCreatedCampaignId] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  // Determine which steps array to use based on workflow type
  const getSteps = () => {
    if (profilesLoaded && !hasExistingProfiles) return noProfilesSteps;
    if (workflowType === 'existing') return existingProfileSteps;
    return newProfileSteps;
  };
  
  const steps = getSteps();

  useEffect(() => {
    // Track page view
    trackEvent('campaign_creator_viewed', { userId: user?.id });
  }, [user]);

  const generateDefaultCampaignName = (analysisData, campaignGoal) => {
    // Handle nested business data structure
    const businessData = analysisData?.businessData || analysisData;
    const businessName = businessData?.businessName || businessData?.business_name || businessData?.name || 'Business';
    const goalLabels = {
      'product_launch': 'Product Launch',
      'brand_awareness': 'Brand Awareness',
      'event_promotion': 'Event Promotion',
      'sales_promotion': 'Sales Campaign',
      'content_promotion': 'Content Campaign'
    };
    
    const goalLabel = goalLabels[campaignGoal] || 'Marketing Campaign';
    const currentDate = new Date().toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
    
    return `${businessName} ${goalLabel} - ${currentDate}`;
  };

  const handleProfileSelection = (profile) => {
    setSelectedBusinessProfile(profile);
    setError(null);
    
    if (profile) {
      // User selected existing profile - go to goal selection
      setWorkflowType('existing');
      
      trackEvent('existing_business_profile_selected', {
        userId: user?.id,
        profileId: profile.id,
        profileName: profile.name
      });
      
      // Set up campaign data with existing profile data
      setCampaignData(prev => ({
        ...prev,
        websiteUrl: profile.url,
        campaignName: prev.campaignName || generateDefaultCampaignName(profile, prev.campaignGoal || 'brand_awareness')
      }));
      
      // Use existing profile data as website analysis
      setWebsiteAnalysis({
        businessData: {
          businessName: profile.name,
          description: profile.description,
          industry: profile.industry,
          business_type: profile.business_type,
          url: profile.url,
          has_brand_assets: profile.has_brand_assets,
          has_complete_profile: profile.has_complete_profile
        }
      });
      
      setAnalysisComplete(true);
      
      // Move to goal selection step (step 1 in existing profile workflow)
      setActiveStep(1);
    } else {
      // User chose to create new profile - continue to website entry
      setWorkflowType('new');
      
      trackEvent('new_business_profile_creation_selected', {
        userId: user?.id
      });
      
      // Move to website & goal form step (step 1 in new profile workflow)
      setActiveStep(1);
    }
  };

  const handleBusinessProfilesLoaded = (profilesData) => {
    const hasProfiles = profilesData && profilesData.length > 0;
    setHasExistingProfiles(hasProfiles);
    setProfilesLoaded(true); // Mark that profiles have been loaded
    
    // If user has no existing profiles, skip profile selection step and go to website form
    if (!hasProfiles) {
      setWorkflowType('new'); // Set workflow type for no profiles
      // activeStep stays at 0, which will show the website form for no-profiles workflow
    }
    // If user has existing profiles, workflowType stays null and activeStep stays at 0
    // This will show the BusinessProfileSelector as the first step
  };

  const handleGoalSelection = (formData) => {
    setError(null);
    
    // Update campaign data with goal selection
    setCampaignData(prev => ({
      ...prev,
      campaignName: formData.campaignName,
      campaignGoal: formData.campaignGoal,
      description: formData.description,
      websiteUrl: formData.websiteUrl
    }));
    
    trackEvent('campaign_goal_selected', {
      userId: user?.id,
      goal: formData.campaignGoal,
      profileId: selectedBusinessProfile?.id,
      workflowType: 'existing'
    });
    
    // Move to review step (step 2 in existing profile workflow)
    setActiveStep(2);
  };

  const handleFormSubmit = async (formData) => {
    setError(null);
    setIsAnalyzing(true);
    setCampaignData(formData);
    
    // Move to analyzing step (step 2 in new profile workflow)
    setActiveStep(2);

    try {
      const response = await campaignApi.analyzeWebsite({
        url: formData.websiteUrl,
        userId: user?.id
      });

      if (response.success && response.data) {
        const analysisData = response.data;
        
        // Generate default campaign name if not provided or update existing one
        const defaultName = generateDefaultCampaignName(analysisData, formData.campaignGoal);
        const finalCampaignName = formData.campaignName || defaultName;
        
        // Update campaign data with analysis and final name
        setCampaignData(prev => ({
          ...prev,
          campaignName: finalCampaignName
        }));
        
        setWebsiteAnalysis(analysisData);
        setAnalysisComplete(true);
        
        trackEvent('website_analysis_completed', {
          userId: user?.id,
          websiteUrl: formData.websiteUrl,
          businessName: analysisData?.businessData?.businessName || analysisData?.businessName,
          workflowType: workflowType
        });
      } else {
        throw new Error('Website analysis failed');
      }
    } catch (error) {
      console.error('Website analysis failed:', error);
      setError(error.message || 'Failed to analyze website. Please try again or enter your business details manually.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleContinueToReview = () => {
    // Move to review step (step 3 in new profile workflow, or step 2 if no profiles)
    if (workflowType === 'new' || !hasExistingProfiles) {
      setActiveStep(3);
    } else {
      setActiveStep(2); // This shouldn't happen in the existing profile workflow
    }
  };

  const handleManualInput = async (manualData) => {
    setError(null);
    setShowManualInput(false);
    
    // Generate default campaign name from manual input
    const defaultName = generateDefaultCampaignName(manualData, campaignData.campaignGoal);
    const finalCampaignName = campaignData.campaignName || defaultName;
    
    // Update campaign data with final name
    setCampaignData(prev => ({
      ...prev,
      campaignName: finalCampaignName
    }));
    
    setWebsiteAnalysis(manualData);
    setAnalysisComplete(true);
    
    // Move to review step (step 3 in new profile workflow, or step 2 if no profiles)
    if (workflowType === 'new' || !hasExistingProfiles) {
      setActiveStep(3);
    } else {
      setActiveStep(2); // This shouldn't happen in existing profile workflow
    }
    
    trackEvent('manual_business_input_completed', {
      userId: user?.id,
      websiteUrl: campaignData.websiteUrl,
      businessName: manualData?.businessData?.businessName || manualData?.businessName,
      workflowType: workflowType
    });
  };

  const handleUpdateBusinessInfo = (updatedInfo) => {
    // If the analysis data has nested structure, preserve it
    if (websiteAnalysis?.businessData) {
      setWebsiteAnalysis(prev => ({
        ...prev,
        businessData: updatedInfo
      }));
    } else {
      setWebsiteAnalysis(updatedInfo);
    }
    
    // Regenerate campaign name if business name changed
    const defaultName = generateDefaultCampaignName(updatedInfo, campaignData.campaignGoal);
    setCampaignData(prev => ({
      ...prev,
      campaignName: prev.campaignName || defaultName
    }));
  };

  const handleUpdateCampaignData = (updates) => {
    setCampaignData(prev => ({ ...prev, ...updates }));
  };

  const handleCreateCampaign = async () => {
    try {
      setIsCreatingCampaign(true);
      
      // Move to creation progress step
      if (workflowType === 'existing') {
        setActiveStep(3); // Step 3 in existing profile workflow
      } else {
        setActiveStep(4); // Step 4 in new profile workflow or no profiles
      }
      
      const campaignPayload = {
        name: campaignData.campaignName,
        websiteUrl: campaignData.websiteUrl,
        campaignGoal: campaignData.campaignGoal,
        description: campaignData.description,
        businessData: websiteAnalysis?.businessData || websiteAnalysis
      };

      // Add business profile ID if existing profile was selected
      if (selectedBusinessProfile) {
        campaignPayload.businessProfileId = selectedBusinessProfile.id;
      }
      
      const response = await campaignApi.createCampaign(campaignPayload);
      
      if (response.success) {
        const campaign = response.data.campaign;
        setCreatedCampaignId(campaign.id);
        
        trackEvent('campaign_created', {
          userId: user?.id,
          campaignId: campaign.id,
          campaignName: campaignData.campaignName,
          websiteUrl: campaignData.websiteUrl,
          goal: campaignData.campaignGoal,
          usedExistingProfile: !!selectedBusinessProfile,
          profileId: selectedBusinessProfile?.id,
          workflowType: workflowType
        });

        // Wait a moment to show success state
        setTimeout(() => {
          navigate(`/campaigns/${campaign.id}/review`);
        }, 1500);
      } else {
        throw new Error('Campaign creation failed');
      }
      
    } catch (error) {
      console.error('Campaign creation failed:', error);
      setError(error.message || 'Failed to create campaign');
      
      // Go back to review step on error
      if (workflowType === 'existing') {
        setActiveStep(2); // Review step in existing profile workflow
      } else {
        setActiveStep(3); // Review step in new profile workflow or no profiles
      }
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  const handleBack = () => {
    if (showManualInput) {
      // Back from manual input to analyzing step
      setShowManualInput(false);
      setError(null);
      // Only new profile workflow has analyzing step
      if (workflowType === 'new' || !hasExistingProfiles) {
        setActiveStep(2); // Analyzing step
      }
    } else if (activeStep > 0) {
      // Standard back navigation
      setActiveStep(activeStep - 1);
      setError(null);
      setShowManualInput(false);
    }
  };

  const handleBackToForm = () => {
    // Reset to website entry step
    if (workflowType === 'new' || !hasExistingProfiles) {
      setActiveStep(1); // Website form step
    } else if (workflowType === 'existing') {
      setActiveStep(1); // Goal selection step
    }
    setError(null);
    setShowManualInput(false);
    setWebsiteAnalysis(null);
    setAnalysisComplete(false);
  };

  const handleBackToProfileSelection = () => {
    // Reset to profile selection step
    setActiveStep(0);
    setError(null);
    setShowManualInput(false);
    setWebsiteAnalysis(null);
    setAnalysisComplete(false);
    setSelectedBusinessProfile(null);
    setWorkflowType(null);
    setCampaignData({
      campaignName: '',
      websiteUrl: '',
      campaignGoal: '',
      description: ''
    });
  };

  const renderStepContent = () => {
    // Handle different workflows
    if (profilesLoaded && !hasExistingProfiles) {
      // No profiles workflow: Website & Goal -> Analyzing -> Review -> Creating
    switch (activeStep) {
      case 0:
        return (
          <CampaignForm
            onSubmit={handleFormSubmit}
            initialData={campaignData}
            isLoading={isAnalyzing}
              showBackToProfiles={false}
          />
        );
      case 1:
        return (
          <Box>
            <GenerationProgress
              isAnalyzing={isAnalyzing}
              websiteUrl={campaignData.websiteUrl}
              error={error}
              mode="analyze"
              onComplete={handleContinueToReview}
              onBack={handleBackToForm}
              onManualInput={() => setShowManualInput(true)}
            />
            
            {error && !showManualInput && (
              <Fade in>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    We couldn't automatically analyze your website. You can enter your business details manually.
                  </Alert>
                  <Button
                    variant="outlined"
                    onClick={() => setShowManualInput(true)}
                  >
                    Enter Details Manually
                  </Button>
                </Box>
              </Fade>
            )}

            {showManualInput && (
              <Fade in>
                <Box sx={{ mt: 3 }}>
                  <ManualInputForm
                    onSubmit={handleManualInput}
                    websiteUrl={campaignData.websiteUrl}
                    onCancel={handleBackToForm}
                    cancelLabel="Try Different URL"
                  />
                </Box>
              </Fade>
            )}
          </Box>
        );
        case 2:
          return (
            <WebsitePreview
              analysisData={websiteAnalysis?.businessData || websiteAnalysis}
              campaignData={campaignData}
              onCreateCampaign={handleCreateCampaign}
              isCreating={isCreatingCampaign}
              onUpdateBusinessInfo={handleUpdateBusinessInfo}
              onUpdateCampaignData={handleUpdateCampaignData}
              selectedBusinessProfile={selectedBusinessProfile}
              showProfileInfo={!!selectedBusinessProfile}
            />
          );
        case 3:
          return (
            <GenerationProgress
              isAnalyzing={isCreatingCampaign}
              websiteUrl={campaignData.websiteUrl}
              error={error}
              mode="create"
            />
          );
        default:
          return null;
      }
    } else if (workflowType === 'existing') {
      // Existing profile workflow: Select Profile -> Goal Selection -> Review -> Creating
      switch (activeStep) {
        case 0:
          return (
            <BusinessProfileSelector
              onProfileSelect={handleProfileSelection}
              selectedProfileId={selectedBusinessProfile?.id}
              websiteUrl={campaignData.websiteUrl}
              disabled={isAnalyzing || isCreatingCampaign}
              onProfilesLoaded={handleBusinessProfilesLoaded}
            />
          );
        case 1:
          return (
            <CampaignGoalSelector
              onSubmit={handleGoalSelection}
              selectedBusinessProfile={selectedBusinessProfile}
              initialData={campaignData}
              showBackToProfiles={true}
              onBackToProfiles={handleBackToProfileSelection}
            />
          );
      case 2:
        return (
            <WebsitePreview
              analysisData={websiteAnalysis?.businessData || websiteAnalysis}
              campaignData={campaignData}
              onCreateCampaign={handleCreateCampaign}
              isCreating={isCreatingCampaign}
              onUpdateBusinessInfo={handleUpdateBusinessInfo}
              onUpdateCampaignData={handleUpdateCampaignData}
              selectedBusinessProfile={selectedBusinessProfile}
              showProfileInfo={true}
            />
          );
        case 3:
          return (
            <GenerationProgress
              isAnalyzing={isCreatingCampaign}
              websiteUrl={campaignData.websiteUrl}
              error={error}
              mode="create"
            />
          );
        default:
          return null;
      }
    } else {
      // New profile workflow: Select Profile Type -> Website & Goal -> Analyzing -> Review -> Creating
      switch (activeStep) {
        case 0:
          return (
            <BusinessProfileSelector
              onProfileSelect={handleProfileSelection}
              selectedProfileId={selectedBusinessProfile?.id}
              websiteUrl={campaignData.websiteUrl}
              disabled={isAnalyzing || isCreatingCampaign}
              onProfilesLoaded={handleBusinessProfilesLoaded}
            />
          );
        case 1:
          return (
            <CampaignForm
              onSubmit={handleFormSubmit}
              initialData={campaignData}
              isLoading={isAnalyzing}
              showBackToProfiles={true}
              onBackToProfiles={handleBackToProfileSelection}
            />
          );
        case 2:
          return (
            <Box>
              <GenerationProgress
                isAnalyzing={isAnalyzing}
                websiteUrl={campaignData.websiteUrl}
                error={error}
                mode="analyze"
                onComplete={handleContinueToReview}
                onBack={handleBackToForm}
                onManualInput={() => setShowManualInput(true)}
              />
              
              {error && !showManualInput && (
                <Fade in>
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      We couldn't automatically analyze your website. You can enter your business details manually.
                    </Alert>
                    <Button
                      variant="outlined"
                      onClick={() => setShowManualInput(true)}
                    >
                      Enter Details Manually
                    </Button>
                  </Box>
                </Fade>
              )}

              {showManualInput && (
                <Fade in>
                  <Box sx={{ mt: 3 }}>
                    <ManualInputForm
                      onSubmit={handleManualInput}
                      websiteUrl={campaignData.websiteUrl}
                      onCancel={handleBackToForm}
                      cancelLabel="Try Different URL"
                    />
                  </Box>
                </Fade>
              )}
          </Box>
        );
      case 3:
        return (
            <WebsitePreview
              analysisData={websiteAnalysis?.businessData || websiteAnalysis}
              campaignData={campaignData}
              onCreateCampaign={handleCreateCampaign}
              isCreating={isCreatingCampaign}
              onUpdateBusinessInfo={handleUpdateBusinessInfo}
              onUpdateCampaignData={handleUpdateCampaignData}
              selectedBusinessProfile={selectedBusinessProfile}
              showProfileInfo={!!selectedBusinessProfile}
            />
          );
        case 4:
          return (
            <GenerationProgress
              isAnalyzing={isCreatingCampaign}
              websiteUrl={campaignData.websiteUrl}
              error={error}
              mode="create"
            />
        );
      default:
        return null;
    }
    }
  };

  const getPageTitle = () => {
    if (profilesLoaded && hasExistingProfiles && activeStep === 0 && !workflowType) {
      return 'Create New Campaign';
    }
    return 'Create New Campaign';
  };

  const getPageSubtitle = () => {
    if (profilesLoaded && hasExistingProfiles && activeStep === 0 && !workflowType) {
      return 'Select an existing business profile or create a new one';
    }
    return 'Generate a complete social media campaign from your website in minutes';
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {getPageTitle()}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {getPageSubtitle()}
        </Typography>

        <StyledPaper>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && !showManualInput && (
            // Only show error alert outside of the analyzing step
            (workflowType === 'existing' || (!hasExistingProfiles && activeStep !== 1) || (workflowType === 'new' && activeStep !== 2))
          ) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {renderStepContent()}

          {/* Back button logic */}
          {activeStep > 0 && 
           activeStep < steps.length - 1 && 
           !showManualInput && 
           !error && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
              <Button 
                onClick={handleBack} 
                disabled={isAnalyzing || isCreatingCampaign}
              >
                Back
              </Button>
            </Box>
          )}
        </StyledPaper>
      </Box>
    </Container>
  );
};

export default CampaignCreatorPage; 